/**
 * Token 同步服务
 * 负责在有 Kratos session 但没有 OAuth2 token 时，静默获取 token
 * 使用 OneAuth 的 /api/oauth/web/exchange 接口，无需页面跳转
 */

import { isLoggedIn } from './kratosService';
import { generateRandomString, generateCodeChallenge } from './oauth2Service';

let syncInProgress = false;
let syncPromise = null;
let isLoggingOut = false; // 登出标志，防止登出时自动获取 token

// OneAuth 配置
const ONEAUTH_URL = import.meta.env.VITE_ONEAUTH_URL || 'http://localhost:8080';

// OAuth2 配置 - 统一使用同一个 client
const OAUTH_CONFIG = {
  clientId: import.meta.env.VITE_OAUTH_CLIENT_ID || 'synnovator-web',
  redirectUri: import.meta.env.VITE_OAUTH_REDIRECT_URI || 'http://localhost:3001/callback',
  scope: import.meta.env.VITE_OAUTH_SCOPE || 'openid profile email offline',
};

/**
 * 检查并静默获取 OAuth2 token
 * 如果有 Kratos session 但没有 access_token，调用 OneAuth exchange API 静默获取
 * @param {boolean} forceRefresh - 是否强制刷新（即使已有 token，用于 token 失效时）
 * @returns {Promise<boolean>} 是否成功获取 token
 */
export async function ensureOAuth2Token(forceRefresh = false) {
  // If a sync is already running, wait for it.
  if (syncPromise) {
    console.log('[TokenSync] Sync already in progress, waiting...');
    return syncPromise;
  }

  // 登出过程中跳过 token 同步
  if (isLoggingOut) {
    console.log('[TokenSync] Logout in progress, skipping token sync');
    return false;
  }

  syncInProgress = true;
  syncPromise = (async () => {
    try {
      // 再次检查登出标志（异步操作期间可能已设置）
      if (isLoggingOut) {
        console.log('[TokenSync] Logout in progress, aborting token sync');
        return false;
      }

      // 1. 检查本地是否已有 OAuth2 token
      if (!forceRefresh) {
        const accessToken = localStorage.getItem('syntrust_access_token');

        if (accessToken) {
          console.log('✅ OAuth2 token already exists (use forceRefresh=true to refresh)');
          return true;
        }
        console.log('🔍 No OAuth2 token found, checking Kratos session...');
      } else {
        console.log('🔄 Force refresh requested, will get new OAuth2 token');
      }

      // 2. Check Kratos session.
      const hasKratosSession = await isLoggedIn();

      if (!hasKratosSession) {
        console.log('[TokenSync] No Kratos session, user needs to login');
        return false;
      }

      // 3. Kratos session exists, exchange via OneAuth to get OAuth2 token.
      console.log('[TokenSync] Kratos session found, exchanging for OAuth2 token...');

      // Generate PKCE params.
      const codeVerifier = generateRandomString(64);
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      // Call OneAuth exchange API.
      console.log('[TokenSync] Calling OneAuth exchange API:', `${ONEAUTH_URL}/api/oauth/web/exchange`);
      const response = await fetch(`${ONEAUTH_URL}/api/oauth/web/exchange`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send Kratos session cookie.
        body: JSON.stringify({
          client_id: OAUTH_CONFIG.clientId,
          redirect_uri: OAUTH_CONFIG.redirectUri,
          scope: OAUTH_CONFIG.scope,
          code_verifier: codeVerifier,
          code_challenge: codeChallenge,
        }),
      });

      console.log('[TokenSync] Exchange API response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[TokenSync] Exchange API error:', errorData);
        throw new Error(errorData.error || `Exchange failed: ${response.status}`);
      }

      const tokenData = await response.json();
      console.log('[TokenSync] OAuth2 token exchange successful', {
        has_access_token: !!tokenData.access_token,
        has_refresh_token: !!tokenData.refresh_token,
        token_type: tokenData.token_type,
        expires_in: tokenData.expires_in,
      });

      // Save tokens.
      if (tokenData.access_token) {
        // Keep legacy auth token for compatibility with existing api.js usage.
        localStorage.setItem('syntrust_access_token', tokenData.access_token);
        localStorage.setItem('syntrust_auth_token', tokenData.access_token); // Backward compatibility.
        console.log('[TokenSync] Saved access_token to localStorage');

        if (tokenData.refresh_token) {
          localStorage.setItem('syntrust_refresh_token', tokenData.refresh_token);
          console.log('[TokenSync] Saved refresh_token to localStorage');
        }

        if (tokenData.id_token) {
          localStorage.setItem('syntrust_id_token', tokenData.id_token);
          console.log('[TokenSync] Saved id_token to localStorage');
        }

        // Save token expiry.
        if (tokenData.expires_in) {
          const expiresAt = Date.now() + (tokenData.expires_in * 1000);
          localStorage.setItem('syntrust_token_expires_at', expiresAt.toString());
          console.log('[TokenSync] Token expires in:', tokenData.expires_in, 'seconds');
        }

        console.log('[TokenSync] All tokens saved successfully');
        return true;
      }

      console.error('[TokenSync] No access_token in response:', tokenData);
      throw new Error('No access_token in response');
    } catch (error) {
      console.error('[TokenSync] Token sync failed:', error);
      return false;
    } finally {
      syncInProgress = false;
      syncPromise = null;
    }
  })();

  return syncPromise;
}

/**
 * 获取并保存当前用户信息
 * @param {string} accessToken - OAuth2 access token
 */
async function fetchAndSaveCurrentUser(accessToken) {
  try {
    const backendUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';
    const response = await fetch(`${backendUrl}/api/v2/auth/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('❌ Failed to fetch user info:', response.status);
      return;
    }

    const result = await response.json();
    console.log('✅ Current user fetched:', result);

    // 后端返回格式: { user: {...} }
    const userData = result.user || result;

    // 保存用户信息到 localStorage
    localStorage.setItem('syntrust_user', JSON.stringify(userData));
    localStorage.setItem('syntrust_login_status', 'true');
    console.log('💾 User info saved to localStorage');

    // 触发 storage 事件通知其他组件
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'syntrust_user',
      newValue: JSON.stringify(userData),
    }));
  } catch (error) {
    console.error('❌ Error fetching user info:', error);
    // 不抛出错误，允许登录流程继续
  }
}

/**
 * 重置同步状态（测试用）
 */
export function resetSyncState() {
  syncInProgress = false;
}

/**
 * 设置登出标志
 * 登出时调用，防止 tokenSync 自动获取新 token
 * @param {boolean} value - 是否正在登出
 */
export function setLoggingOut(value) {
  isLoggingOut = value;
  if (value) {
    console.log('[TokenSync] Logout flag set, token sync will be blocked');
  }
}

export default {
  ensureOAuth2Token,
  resetSyncState,
  setLoggingOut,
};
